import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentTeam } from '@/features/team/current-team'
import { deleteChats, executeTool, getHistory, streamChat, type StreamData } from './api'
import { fromHistory } from './history'
import { Approval, TOOL_ACCESS, titleCase, type Access, type ChatMessage, type ToolCall } from './types'

interface AssistantState {
  messages: ChatMessage[]
  loading: boolean
  loadFailed: boolean
  /** a reply is streaming or a tool is running */
  busy: boolean
  /** an action waits for approval; the API can't take a new message until it's answered */
  awaiting: boolean
  send: (text: string) => void
  decide: (callId: string, approve: boolean) => void
  stop: () => void
  reset: () => Promise<void>
  retryLoad: () => void
  /** called by a chat view on mount; history loads on first use, not on every page */
  activate: () => void
}

const AssistantContext = createContext<AssistantState | null>(null)

const uid = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`
const accessOf = (level: string | undefined, name: string): Access =>
  level === 'read' || level === 'write' || level === 'delete' ? level : (TOOL_ACCESS[name] ?? 'write')

/**
 * One conversation for the whole app, so the full page and the side panel
 * show the same chat. The flow follows the API: stream a reply; run the
 * read-only tools it asks for at once and continue; hold write tools until
 * the person approves or declines, then continue.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const { ready, current } = useCurrentTeam()
  const teamId = current?.team.Id ?? current?.team.id
  const qc = useQueryClient()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const chatId = useRef<number | undefined>(undefined)
  const abort = useRef<AbortController | null>(null)
  const pendingIds = useRef(new Set<string>())

  const load = useCallback(() => {
    setLoading(true)
    setLoadFailed(false)
    getHistory().then(
      (items) => {
        const restored = fromHistory(items)
        pendingIds.current = new Set(restored.flatMap((m) => m.tools.filter((c) => c.status === 'pending').map((c) => c.id)))
        setMessages(restored)
        setLoading(false)
      },
      () => {
        setLoadFailed(true)
        setLoading(false)
      },
    )
  }, [])

  const [wanted, setWanted] = useState(false)
  const loaded = useRef(false)
  useEffect(() => {
    if (ready && wanted && !loaded.current) {
      loaded.current = true
      load()
    }
  }, [ready, wanted, load])
  const activate = useCallback(() => setWanted(true), [])

  // chats belong to a team: switching teams starts from that team's chat
  const lastTeam = useRef(teamId)
  useEffect(() => {
    if (lastTeam.current === teamId) return
    lastTeam.current = teamId
    abort.current?.abort()
    chatId.current = undefined
    pendingIds.current = new Set()
    setMessages([])
    loaded.current = false
    if (ready && wanted) {
      loaded.current = true
      load()
    }
  }, [teamId, ready, wanted, load])

  const patch = (id: string, fn: (m: ChatMessage) => ChatMessage) => setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)))
  const patchCall = (callId: string, fields: Partial<ToolCall>) =>
    setMessages((prev) => prev.map((m) => (m.tools.some((c) => c.id === callId) ? { ...m, tools: m.tools.map((c) => (c.id === callId ? { ...c, ...fields } : c)) } : m)))

  const execute = async (call: { id: string; access: Access }, approval: number) => {
    patchCall(call.id, { status: 'running' })
    try {
      const r = await executeTool(call.id, approval)
      patchCall(call.id, {
        status: r.status === 'denied' ? 'denied' : r.success ? 'completed' : 'failed',
        result: r.result ?? null,
        error: r.error ?? null,
      })
      // a write may have changed companies, the team or its members
      if (r.success && call.access !== 'read') qc.invalidateQueries()
    } catch (e) {
      const gone = (e as { response?: { status?: number } }).response?.status === 404
      patchCall(call.id, { status: 'failed', error: gone ? 'gone' : 'network' })
    }
  }

  const run = async (data: StreamData): Promise<void> => {
    const id = uid()
    setMessages((prev) => [...prev, { id, role: 'assistant', text: '', tools: [], at: new Date().toISOString(), streaming: true }])
    const calls: ToolCall[] = []
    const ctrl = new AbortController()
    abort.current = ctrl
    let failed = false

    try {
      await streamChat(
        { chat_id: chatId.current, context: { current_user_location: location.pathname + location.search }, data },
        ctrl.signal,
        (ev) => {
          if (ev.type === 'meta') chatId.current = ev.id
          else if (ev.type === 'chunk') patch(id, (m) => ({ ...m, text: m.text + ev.text }))
          else {
            const c = ev.call
            const access = accessOf(c.accessLevel, c.name)
            const call: ToolCall = {
              id: c.callId,
              name: c.name,
              displayName: c.displayName || titleCase(c.name),
              access,
              args: c.arguments ?? {},
              summary: c.summary,
              status: access === 'read' ? 'running' : 'pending',
            }
            calls.push(call)
            patch(id, (m) => ({ ...m, tools: [...m.tools, call] }))
          }
        },
      )
    } catch {
      failed = !ctrl.signal.aborted
    }
    abort.current = null
    // drop a reply that never said anything
    setMessages((prev) =>
      prev.flatMap((m) => (m.id !== id ? [m] : !m.text.trim() && !m.tools.length && !failed ? [] : [{ ...m, streaming: false, failed }])),
    )
    if (failed || ctrl.signal.aborted) return

    const reads = calls.filter((c) => c.access === 'read')
    const writes = calls.filter((c) => c.access !== 'read')
    for (const c of reads) await execute(c, Approval.System)
    writes.forEach((c) => pendingIds.current.add(c.id))
    // continue once the tool output is in; writes wait for decide()
    if (reads.length && !writes.length) return run(null)
  }

  const send = (text: string) => {
    const content = text.trim()
    if (!content || busy || pendingIds.current.size) return
    setMessages((prev) => [...prev, { id: uid(), role: 'user', text: content, tools: [], at: new Date().toISOString() }])
    setBusy(true)
    run({ type: 'message', content }).finally(() => setBusy(false))
  }

  const decide = (callId: string, approve: boolean) => {
    if (busy || !pendingIds.current.has(callId)) return
    const access = messages.flatMap((m) => m.tools).find((c) => c.id === callId)?.access ?? 'write'
    setBusy(true)
    ;(async () => {
      await execute({ id: callId, access }, approve ? Approval.Approved : Approval.Denied)
      pendingIds.current.delete(callId)
      // the reply continues with every answer in, including a decline
      if (!pendingIds.current.size) await run(null)
    })().finally(() => setBusy(false))
  }

  const stop = () => abort.current?.abort()

  const reset = async () => {
    stop()
    await deleteChats()
    chatId.current = undefined
    pendingIds.current = new Set()
    setMessages([])
  }

  const awaiting = messages.some((m) => m.tools.some((c) => c.status === 'pending'))

  const value = useMemo<AssistantState>(
    () => ({ messages, loading, loadFailed, busy, awaiting, send, decide, stop, reset, retryLoad: load, activate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, loading, loadFailed, busy, awaiting],
  )
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export function useAssistant() {
  const ctx = useContext(AssistantContext)
  if (!ctx) throw new Error('useAssistant must be used inside AssistantProvider')
  return ctx
}
